/**
 * Not Found Page (404)
 *
 * Every unknown address — and a flagged-off feature (`FeatureFlagRoute`).
 * A signed-in citizen reads it inside the app shell, so their navigation stays
 * in reach like on every other screen; a signed-out visitor gets it on its own.
 * A wrong address is a navigation miss, not an error: the 404 badge is neutral.
 */

import { useCallback } from 'react';
import { Link, useLocation } from 'wouter';
import { useTranslation } from '@tonnie/locale';
import { usePageView } from '@tonnie/observability';
import { ArrowLeft, Home, MapPin, TonnieLogo } from '@tonnie/assets';
import { Button } from '@tonnie/ui';

import { AppShellLayout } from '@/components/layout/AppShellLayout';
import { LoadingSpinner } from '@/components/shell/LoadingSpinner';
import { useAuth } from '@/core/contexts/AuthContext';

type NotFoundLayout = 'shell' | 'standalone';

function NotFoundContent({ layout }: { layout: NotFoundLayout }) {
  const { t } = useTranslation();
  const [location] = useLocation();

  const handleGoBack = useCallback(() => {
    window.history.back();
  }, []);

  return (
    <div
      data-testid="not-found-page"
      data-layout={layout}
      className="w-full max-w-md mx-auto flex flex-col items-center text-center animate-in fade-in slide-in-from-bottom-6 duration-500"
    >
      {/* The shell already carries the logo. */}
      {layout === 'standalone' && (
        <div className="mb-10">
          <TonnieLogo variant="symbol" size="md" />
        </div>
      )}

      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
          <MapPin className="w-10 h-10 text-primary-accessible" aria-hidden="true" />
        </div>
        <div
          className="absolute -top-2 -right-2 w-8 h-8 rounded-full border border-border bg-muted text-muted-foreground text-xs font-bold flex items-center justify-center"
          aria-hidden="true"
        >
          404
        </div>
      </div>

      <h1 className="text-2xl font-bold text-foreground mb-2">{t('notFound.title')}</h1>

      <p className="text-muted-foreground text-sm leading-relaxed mb-2 max-w-xs">
        {t('notFound.description')}
      </p>

      {/* The attempted address, for context. */}
      {location && location !== '/' && (
        <p className="text-xs text-muted-foreground font-mono mb-8 break-all">{location}</p>
      )}

      {!location || location === '/' ? <div className="mb-8" /> : null}

      <div className="flex flex-col gap-3 w-full max-w-xs">
        <Button className="w-full gap-2" size="lg" asChild>
          <Link href={layout === 'shell' ? '/dashboard' : '/'}>
            <Home className="w-4 h-4" aria-hidden="true" />
            {t('notFound.goHome')}
          </Link>
        </Button>

        <Button variant="outline" size="lg" className="w-full gap-2" onClick={handleGoBack}>
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          {t('notFound.goBack')}
        </Button>
      </div>
    </div>
  );
}

export function NotFoundPage() {
  const { isLoading, isAuthenticated } = useAuth();
  usePageView('not_found');

  if (isLoading) return <LoadingSpinner />;

  if (isAuthenticated) {
    return (
      <AppShellLayout>
        <div className="py-12 px-4">
          <NotFoundContent layout="shell" />
        </div>
      </AppShellLayout>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <NotFoundContent layout="standalone" />
    </div>
  );
}

export default NotFoundPage;
